import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaSignOutAlt, FaFileAlt, FaUser } from 'react-icons/fa';
import { useMobile } from '@/hooks/useMobile';

const candidateNavLinks = [
  { name: 'Candidate Application Status', path: '/candidate/application-status', icon: <FaFileAlt /> },
  { name: 'Candidate Information', path: '/candidate/information', icon: <FaUser /> },
];

const CandidateLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('candidateToken');
    localStorage.removeItem('candidateEmail');
    navigate('/candidate-login');
    setIsDropdownOpen(false);
  };

  // Get candidate info from localStorage
  const candidateEmail = localStorage.getItem('candidateEmail') || 'Candidate';

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center justify-between px-6 border-b">
          <div className="font-bold text-xl text-blue-600">Club Corners</div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {candidateNavLinks.map(link => (
            <Link
              key={link.name}
              to={link.path}
              className={`flex items-center px-6 py-3 mb-2 text-gray-700 hover:bg-blue-50 transition rounded-r-full mx-2 ${
                location.pathname === link.path ? 'bg-blue-100 font-semibold text-blue-700' : ''
              }`}
            >
              <span className="mr-3 text-lg">{link.icon}</span>
              <span className="text-sm lg:text-base">{link.name}</span>
            </Link>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 z-1">
        {/* Header */}
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
            >
              <FaBars className="w-5 h-5" />
            </button>
            <div className={`font-bold ${isMobile ? 'text-xl text-blue-600' : 'text-lg text-gray-900'} whitespace-nowrap`}>
              {isMobile ? 'Club Corners' : 'Candidate Dashboard'}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                {candidateEmail.charAt(0).toUpperCase()}
              </div>
              <span className="text-gray-700 font-medium hidden sm:block truncate max-w-32">
                {candidateEmail}
              </span>
              <FaChevronDown className={`text-gray-500 transition-transform flex-shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {candidateEmail}
                  </div>
                  <div className="text-xs text-gray-500">Candidate</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaSignOutAlt className="mr-3 text-gray-400 flex-shrink-0" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CandidateLayout;

